/* ==========================================================================
   tire-finder.js — dependent Brand > Model > Size selects over a collection.
   ONE implementation, shared by every host that needs a finder. Hosts supply
   their own markup and CSS; this file owns only the behaviour, so the two
   copies cannot drift apart (the way tire pricing did, twice).

   Markup contract — any element with [data-tire-finder]:
     data-collection="tires"              collection handle to read
     data-mode="navigate" | "result"      what submitting does (default: navigate)
   Inside it:
     [data-tf-brand] [data-tf-model] [data-tf-size]   <select> elements
     [data-tf-submit]                                  optional button
     [data-tf-error]                                   optional inline error node
     [data-tf-result]                                  required for mode="result"
       [data-tf-result-name] [data-tf-result-price] [data-tf-result-link]
   ========================================================================== */
(function () {
  'use strict';

  var PAGE_SIZE = 250;

  function formatMoney(cents) {
    var n = Number(cents);
    if (isNaN(n)) return '';
    // products.json reports price as a decimal string ("189.95"), the AJAX API
    // as integer cents. Treat a value with a decimal point as already-dollars.
    return '$' + n.toFixed(2);
  }

  // Walk every page of products.json — the previous single-request version
  // stopped at 250 products and silently hid everything past it.
  function fetchAllProducts(handle) {
    var all = [];

    function page(n) {
      var url = '/collections/' + encodeURIComponent(handle) +
                '/products.json?limit=' + PAGE_SIZE + '&page=' + n;
      return fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var batch = (data && data.products) || [];
          all = all.concat(batch);
          if (batch.length === PAGE_SIZE && n < 20) return page(n + 1);
          return all;
        });
    }

    return page(1);
  }

  function buildIndex(products) {
    // { brand: { model: [ {size, price, url} ] } }
    var index = {};

    products.forEach(function (product) {
      var brand = (product.vendor || '').trim();
      if (!brand) return;

      var model = (product.title || '').replace(/\s+Tire$/i, '').trim();
      if (!model) return;

      var url = product.handle ? '/products/' + product.handle : null;
      if (!url) return;

      (product.variants || []).forEach(function (variant) {
        if (variant.available === false) return;

        var size = (variant.title || variant.option1 || '').trim();
        if (!size || size === 'Default Title') return;

        if (!index[brand]) index[brand] = {};
        if (!index[brand][model]) index[brand][model] = [];

        index[brand][model].push({
          size: size,
          price: variant.price,
          url: url + '?variant=' + variant.id
        });
      });
    });

    return index;
  }

  function fill(select, values, placeholder) {
    select.innerHTML = '';
    var blank = document.createElement('option');
    blank.value = '';
    blank.textContent = placeholder;
    select.appendChild(blank);

    values.forEach(function (value) {
      var opt = document.createElement('option');
      opt.value = value;
      opt.textContent = value;
      select.appendChild(opt);
    });
  }

  function init(root) {
    if (root.hasAttribute('data-tf-ready')) return;
    root.setAttribute('data-tf-ready', '');

    var handle = root.getAttribute('data-collection') || 'tires';
    var mode = root.getAttribute('data-mode') || 'navigate';

    var brandSel = root.querySelector('[data-tf-brand]');
    var modelSel = root.querySelector('[data-tf-model]');
    var sizeSel = root.querySelector('[data-tf-size]');
    var submit = root.querySelector('[data-tf-submit]');
    var errorEl = root.querySelector('[data-tf-error]');
    var resultEl = root.querySelector('[data-tf-result]');

    if (!brandSel || !modelSel || !sizeSel) return;

    var index = {};
    var loaded = false;

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    function selected() {
      var list = (index[brandSel.value] || {})[modelSel.value] || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].size === sizeSel.value) return list[i];
      }
      return null;
    }

    function showResult() {
      if (!resultEl) return;
      var match = selected();
      if (!match) { resultEl.hidden = true; return; }

      var name = resultEl.querySelector('[data-tf-result-name]');
      var price = resultEl.querySelector('[data-tf-result-price]');
      var link = resultEl.querySelector('[data-tf-result-link]');

      if (name) name.textContent = brandSel.value + ' ' + modelSel.value + ' ' + match.size;
      if (price) price.textContent = formatMoney(match.price);
      if (link) link.setAttribute('href', match.url);
      resultEl.hidden = false;
    }

    function onBrand() {
      var models = Object.keys(index[brandSel.value] || {}).sort();
      fill(modelSel, models, 'Model');
      fill(sizeSel, [], 'Size');
      modelSel.disabled = models.length === 0;
      sizeSel.disabled = true;
      if (resultEl) resultEl.hidden = true;
      showError('');
    }

    function onModel() {
      var entries = (index[brandSel.value] || {})[modelSel.value] || [];
      var sizes = entries.map(function (e) { return e.size; })
        .filter(function (s, i, a) { return a.indexOf(s) === i; })
        .sort();
      fill(sizeSel, sizes, 'Size');
      sizeSel.disabled = sizes.length === 0;
      if (resultEl) resultEl.hidden = true;
      showError('');
    }

    function onSize() {
      showError('');
      if (mode === 'result') showResult();
    }

    function go(event) {
      if (event) event.preventDefault();
      if (!sizeSel.value) {
        showError('Choose a size to continue.');
        (sizeSel.disabled ? (modelSel.disabled ? brandSel : modelSel) : sizeSel).focus();
        return;
      }
      var match = selected();
      if (!match) { showError('That combination is not in stock right now.'); return; }
      window.location.href = match.url;
    }

    function load() {
      if (loaded) return;
      loaded = true;

      fetchAllProducts(handle)
        .then(function (products) {
          index = buildIndex(products);
          var brands = Object.keys(index).sort();
          fill(brandSel, brands, 'Brand');
          brandSel.disabled = brands.length === 0;
          if (brands.length === 0) showError('Tires are unavailable right now.');
        })
        .catch(function (err) {
          if (window.console) console.error('[tire-finder] load failed:', err);
          showError('Tires are unavailable right now.');
        });
    }

    brandSel.addEventListener('change', onBrand);
    modelSel.addEventListener('change', onModel);
    sizeSel.addEventListener('change', onSize);
    if (submit && mode !== 'result') submit.addEventListener('click', go);

    modelSel.disabled = true;
    sizeSel.disabled = true;

    // Only fetch the catalog once the widget is actually near the viewport.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { io.disconnect(); load(); }
        });
      }, { rootMargin: '200px' });
      io.observe(root);
    } else {
      load();
    }
  }

  function boot() {
    document.querySelectorAll('[data-tire-finder]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
